/**
 * Loading overlays for "generating" creative states.
 *
 * - `BorderTravelOverlay` → the "snake" traveling-gradient border. Works on any
 *                           aspect ratio thanks to SVG pathLength normalisation.
 *
 * All keyframes are injected once here so they are shared across components.
 *
 * Perf / accessibility notes:
 *   - Respects `prefers-reduced-motion` — users with the OS/browser setting
 *     enabled see a static border instead of a traveling snake.
 *   - Pauses the animation while the tab is hidden so N backgrounded cards
 *     don't burn CPU/battery. A single module-level `visibilitychange`
 *     listener is shared across all overlay instances via `useDocumentVisible`.
 */
import { useEffect, useState } from 'react'

// ─── Shared visibility subscription ──────────────────────────────────────────
// One listener for the whole module so N mounted overlays don't each add a
// `visibilitychange` handler. Each overlay subscribes via useDocumentVisible().

type VisibilityListener = (visible: boolean) => void
const visibilityListeners = new Set<VisibilityListener>()
let visibilityBound = false

function currentVisibility(): boolean {
  if (typeof document === 'undefined') return true
  return !document.hidden
}

function ensureVisibilityBinding() {
  if (visibilityBound) return
  if (typeof document === 'undefined') return
  document.addEventListener('visibilitychange', () => {
    const visible = !document.hidden
    visibilityListeners.forEach(l => l(visible))
  })
  visibilityBound = true
}

function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(currentVisibility)
  useEffect(() => {
    ensureVisibilityBinding()
    const listener: VisibilityListener = next => setVisible(next)
    visibilityListeners.add(listener)
    return () => {
      visibilityListeners.delete(listener)
    }
  }, [])
  return visible
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(REDUCED_MOTION_QUERY).matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(REDUCED_MOTION_QUERY)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    // Safari < 14 only supports the deprecated addListener API.
    if (mql.addEventListener) mql.addEventListener('change', onChange)
    else mql.addListener(onChange)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange)
      else mql.removeListener(onChange)
    }
  }, [])
  return reduced
}

const KEYFRAMES_ID = 'creative-loading-keyframes'

function ensureKeyframes() {
  if (typeof document === 'undefined') return
  if (document.getElementById(KEYFRAMES_ID)) return
  const style = document.createElement('style')
  style.id = KEYFRAMES_ID
  style.textContent = `
    /* Traveling gradient segment on the border. pathLength="100" so the
       same keyframe works on any aspect ratio without knowing the perimeter. */
    @keyframes creative-border-travel-pct {
      0%   { stroke-dashoffset: 100; }
      100% { stroke-dashoffset: 0; }
    }

    /* "Just posted" success flash */
    @keyframes creative-posted-in {
      0%   { opacity: 0; transform: scale(0.85); }
      15%  { opacity: 1; transform: scale(1.05); }
      25%  { transform: scale(1); }
      75%  { opacity: 1; }
      100% { opacity: 0; }
    }

    /* Circular clip-path reveal for the platform logo */
    @keyframes creative-logo-circle-reveal {
      0%   { clip-path: circle(5% at 50% 50%);  opacity: 0.4; }
      40%  { clip-path: circle(55% at 50% 50%); opacity: 1; }
      70%  { clip-path: circle(55% at 50% 50%); opacity: 1; }
      100% { clip-path: circle(5% at 50% 50%);  opacity: 0.4; }
    }

    /* Soft radial pulse used for the platform-icon glow */
    @keyframes creative-platform-pulse {
      0%, 100% { opacity: 0.45; transform: scale(0.92); }
      50%       { opacity: 0.9;  transform: scale(1.08); }
    }

    /* Platform-icon stack settling from the card center to its top-left
       resting spot the moment generation completes. Animates left/top so it
       works without knowing the card's pixel size; 0% matches the centered
       generating position so a still-mounted card hands off seamlessly and a
       freshly-mounted "just finished" card plays the same motion. */
    @keyframes creative-icons-settle {
      0%   { left: 50%;     top: 50%;     transform: translate(-50%, -50%) scale(1.6); }
      100% { left: 0.5rem;  top: 0.5rem;  transform: translate(0, 0) scale(1); }
    }

    /* Respect prefers-reduced-motion by pausing every creative animation.
       Applied globally (not per-component) so the rule covers both the
       React-rendered overlays here AND the inline SVG snake inside
       CreativeCard's in-card "generating" empty state. */
    @media (prefers-reduced-motion: reduce) {
      [style*="creative-border-travel-pct"],
      [style*="creative-posted-in"],
      [style*="creative-logo-circle-reveal"],
      [style*="creative-platform-pulse"] {
        animation-play-state: paused !important;
      }
    }
  `
  document.head.appendChild(style)
}

// ─── Snake border (works on any aspect ratio) ────────────────────────────────
// Uses SVG pathLength="100" so stroke-dasharray/offset values are always
// expressed as % of the perimeter — no need to know the card's dimensions.

export function BorderTravelOverlay({
  speed = 4.6,
  radius = 12,
}: {
  speed?: number
  /**
   * Corner radius (px) of the parent card. Must match the parent's
   * `rounded-*` so the comet bends around corners cleanly instead of
   * being clipped by `overflow-hidden`. Defaults to 12 (`rounded-xl`).
   */
  radius?: number
}) {
  ensureKeyframes()
  const visible = useDocumentVisible()
  const reducedMotion = usePrefersReducedMotion()

  // Pause when the tab is backgrounded or prefers-reduced-motion is on.
  // animation-play-state preserves the snake's position across pause/resume.
  const animate = visible && !reducedMotion

  return (
    <svg
      // z-30 beats everything inside the card (arrows/scrims top out at z-20)
      // but stays under the z-40 assistant dock: the card creates no stacking
      // context, so this z-index competes at the ROOT level and z-50 rode on
      // top of the floating chat panel.
      className="pointer-events-none absolute inset-0 z-30 h-full w-full overflow-visible"
      aria-hidden="true"
    >
      {/* Traveling border snake — 8 % of perimeter, loops every `speed` seconds.
          The card's own border is the visible outline; only the moving comet
          rides on top so we don't stack a second line. Small percentage inset
          keeps the stroke just inside the parent's rounded clip; matching the
          parent's corner radius is what stops the comet from being sliced
          where it bends around the corners. */}
      <rect
        x="1%"
        y="1%"
        width="98%"
        height="98%"
        rx={radius}
        ry={radius}
        fill="none"
        stroke="var(--acid-text, var(--acid))"
        strokeWidth="2.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        pathLength="100"
        strokeDasharray="8 92"
        style={{
          animation: `creative-border-travel-pct ${speed}s linear infinite`,
          animationPlayState: animate ? 'running' : 'paused',
          filter:
            'drop-shadow(0 0 4px color-mix(in srgb, var(--acid-text, var(--acid)) 50%, transparent))',
        }}
      />
    </svg>
  )
}

export { ensureKeyframes }
